/**
 * useAutoSave - hook reutilisable pour formulaire auto-enregistre.
 *
 * Strategie "Create-then-Update" :
 *  - Tant que la fiche n'existe pas, on attend que la condition `canCreate(state)` soit vraie.
 *  - Une fois la condition vraie, on appelle `createFn(state)` -> renvoie { id, doc }.
 *  - Apres creation, chaque changement de `state` declenche un `updateFn(id, diff)` debounce 600ms.
 *  - Retourne `{ status, savedAt, recordId, error }`.
 *
 * Status :
 *  - 'idle'      : en attente de la condition de creation
 *  - 'saving'    : POST/PUT en cours
 *  - 'saved'     : enregistre, savedAt = timestamp
 *  - 'error'     : erreur, error = message
 *
 * Usage type (AddLeadModal) :
 *   const { status, savedAt, recordId } = useAutoSave({
 *     state: form,
 *     canCreate: (s) => /\S+@\S+/.test(s.email || ''),
 *     createFn: async (s) => {
 *       const r = await axios.post(`${API}/crm/leads`, s);
 *       return { id: r.data.lead_id, doc: r.data };
 *     },
 *     updateFn: async (id, s) => {
 *       await axios.put(`${API}/crm/leads/${id}`, s);
 *     },
 *     debounceMs: 600,
 *   });
 */
import { useEffect, useRef, useState } from 'react';

export const useAutoSave = ({
  state,
  canCreate = () => false,
  createFn,
  updateFn,
  debounceMs = 600,
  enabled = true,
}) => {
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [savedAt, setSavedAt] = useState(null);
  const [recordId, setRecordId] = useState(null);
  const [error, setError] = useState(null);

  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const latestStateRef = useRef(state);
  const recordIdRef = useRef(null);

  useEffect(() => { latestStateRef.current = state; }, [state]);
  useEffect(() => { recordIdRef.current = recordId; }, [recordId]);

  useEffect(() => {
    if (!enabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      if (inFlightRef.current) return;

      const currentState = latestStateRef.current;
      const id = recordIdRef.current;

      // CREATE mode
      if (!id) {
        if (!canCreate(currentState)) return;
        inFlightRef.current = true;
        setStatus('saving');
        try {
          const { id: newId } = await createFn(currentState);
          setRecordId(newId);
          recordIdRef.current = newId;
          setStatus('saved');
          setSavedAt(new Date());
          setError(null);
        } catch (e) {
          setStatus('error');
          setError(e.response?.data?.detail || e.message || 'Erreur de sauvegarde');
        } finally {
          inFlightRef.current = false;
        }
        return;
      }

      // UPDATE mode
      inFlightRef.current = true;
      setStatus('saving');
      try {
        await updateFn(id, currentState);
        setStatus('saved');
        setSavedAt(new Date());
        setError(null);
      } catch (e) {
        setStatus('error');
        setError(e.response?.data?.detail || e.message || 'Erreur de sauvegarde');
      } finally {
        inFlightRef.current = false;
      }
    }, debounceMs);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [state, enabled, canCreate, createFn, updateFn, debounceMs]);

  return { status, savedAt, recordId, setRecordId, error };
};

/**
 * Indicateur visuel a afficher dans le footer du formulaire.
 */
export const AutoSaveIndicator = ({ status, savedAt, error }) => {
  const getRelative = () => {
    if (!savedAt) return '';
    const diff = Math.floor((Date.now() - savedAt.getTime()) / 1000);
    if (diff < 5) return 'a l\'instant';
    if (diff < 60) return `il y a ${diff}s`;
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    return savedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };
  if (status === 'idle') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-400" data-testid="autosave-idle">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span>
        En attente de saisie...
      </span>
    );
  }
  if (status === 'saving') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500" data-testid="autosave-saving">
        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        Enregistrement...
      </span>
    );
  }
  if (status === 'saved') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 font-medium" data-testid="autosave-saved">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
        Enregistre {getRelative()}
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-red-600 font-medium" data-testid="autosave-error" title={error}>
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
        </svg>
        Erreur d'enregistrement
      </span>
    );
  }
  return null;
};
